import { groupBy, orderBy } from "lodash";
import { createSelector } from "reselect";

import { getCurrentDeploymentSaleIds } from "./currentSale";
import { getBusinesses, getEContracts } from "./root";
import { selectSaleLotIdsByDeploymentSaleIdLookup } from "./saleLotsCalculated";

export const getEContractById: (
  eContractId: string,
) => (state: State) => EContract | null = eContractId => state =>
  getEContracts(state)[eContractId] || null;

export const selectEContractsInCurrentSaleList: (state: State) => EContract[] =
  createSelector(
    [getCurrentDeploymentSaleIds, getEContracts],
    (deploymentSaleIds: number[], eContracts: Record<string, EContract>) => {
      return Object.values(eContracts).filter(ec =>
        deploymentSaleIds.includes(ec.deploymentSaleId),
      );
    },
  );

export const selectEContracts: (state: State) => Record<string, EContract[]> =
  createSelector(
    [selectEContractsInCurrentSaleList],
    (eContractsInSale: EContract[]) => {
      return groupBy(orderBy(eContractsInSale, ["version"], ["desc"]), "code");
    },
  );

export const selectSaleLotIdsWithoutActiveEContract: (
  state: State,
) => string[] = createSelector(
  [
    getCurrentDeploymentSaleIds,
    selectSaleLotIdsByDeploymentSaleIdLookup,
    selectEContractsInCurrentSaleList,
  ],
  (deploymentSaleIds, saleLotIdsByDeploymentSaleId, eContractsInSale) => {
    const allSaleLotIds = deploymentSaleIds.reduce((acc, deploymentSaleId) => {
      return acc.concat(saleLotIdsByDeploymentSaleId[deploymentSaleId] || []);
    }, [] as string[]);

    if (allSaleLotIds.length === 0) {
      return [];
    }

    const activeEContractSaleLotIds = new Set<string>();
    eContractsInSale
      .filter(ec => ec.id && !ec.voidedOn)
      .forEach(ec => {
        ec.saleLotIds.forEach(id => activeEContractSaleLotIds.add(id));
      });

    return allSaleLotIds.filter(
      saleLotId => !activeEContractSaleLotIds.has(saleLotId),
    );
  },
);

export const getBuyerByEContractId: (
  eContractId: string,
) => (state: State) => Business | null = eContractId => state => {
  const eContract = getEContractById(eContractId)(state);
  if (!eContract) {
    return null;
  }
  return getBusinesses(state)[eContract.buyerBusinessId] || null;
};

export const getVendorByEContractId: (
  eContractId: string,
) => (state: State) => Business | null = eContractId => state => {
  const eContract = getEContractById(eContractId)(state);
  if (!eContract) {
    return null;
  }
  return getBusinesses(state)[eContract.vendorBusinessId] || null;
};
